import { useEffect, useLayoutEffect, useMemo, useState, useRef, useCallback } from 'react'
import { useThree } from '@react-three/fiber'
import { Environment, OrbitControls, ContactShadows } from '@react-three/drei'
import { EffectComposer, N8AO, SMAA } from '@react-three/postprocessing'
import * as THREE from 'three'
import { FBXLoader } from 'three/addons/loaders/FBXLoader.js'
import { RectAreaLightHelper } from 'three/addons/helpers/RectAreaLightHelper.js'
import BedModel from './BedModel'
import Legs from './Legs'
import OttomanLegs from './OttomanLegs'
import BedsideTable from './BedsideTable'
import Ottoman, { useOttomanLegSelection, isSaturnOttoman } from './Ottoman'
import Headboard from './Headboard'
import Topper from './Topper'
import Tray from './Tray'
import Pocket from './Pocket'
import Accessories from './Accessories'
import { useAppStore } from '../../stores/appStore'
import { buildApiUrl, normalizeAssetUrl, isScreenshotEntrypoint } from '../../config'
import { signalScreenshotBedRenderReadyAfterApply } from './screenshotRenderReady'
import { setScreenshotRenderError } from './screenshotRenderError'
import { createNormalizedFBXLoader } from './fbxLoaderUtils'
import { OrbitControls as OrbitControlsImpl } from 'three-stdlib'
import type { DirectionalLightHelper } from 'three'
import { tabCameraPositions, getTabCameraPosition } from './cameraPositions'
import ShadowFloor from './ShadowFloor'


// Expose controls globally so UI components can access them
(window as any).__orbit_controls__ = null

// Helper function to detect mobile devices
const isMobileDevice = (width: number): boolean => {
  return width < 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
}

// Camera adjustment component
function CameraAdjuster() {
  const { camera, size } = useThree()
  const hasAdjusted = useRef(false)
  
  useEffect(() => {
    // Do not auto-tune the camera on the dedicated /screenshot entrypoint;
    // camera there is controlled via URL presets instead.
    if (isScreenshotEntrypoint()) {
      return
    }
    // Only adjust once on mount, based on actual viewport aspect ratio
    // Smaller viewports = more zoomed out (further camera distance)
    if (!hasAdjusted.current && size.width > 0 && size.height > 0) {
      const aspect = size.width / size.height
      const isPortrait = aspect < 1
      const isMobile = size.width < 768
      const viewportArea = size.width * size.height
      
      // Fine-tune camera based on actual canvas viewport size
      // Smaller viewport = more zoomed out
      if (isPortrait && isMobile) {
        // Portrait mobile: most zoomed out for smallest viewport
        camera.position.set(0, 15, 35)
        if ('fov' in camera) {
          (camera as any).fov = 30
          camera.updateProjectionMatrix()
        }
      } else if (viewportArea < 400000) {
        // Small viewport: zoom out more
        camera.position.set(0, 15, 28)
        if ('fov' in camera) {
          (camera as any).fov = 30
          camera.updateProjectionMatrix()
        }
      } else if (viewportArea < 800000) {
        // Medium viewport: medium zoom
        camera.position.set(0, 15, 26)
        if ('fov' in camera) {
          (camera as any).fov = 30
          camera.updateProjectionMatrix()
        }
      }
      // Large viewports keep default camera position
      
      hasAdjusted.current = true
    }
  }, [camera, size])
  
  return null
}

export default function Scene() {
  const controlsRef = useRef<OrbitControlsImpl>(null)
  const { scene, camera, gl, size } = useThree()
  const bedConfig = useAppStore((s) => s.bedConfig)
  const currentModel = useAppStore((s) => s.bedConfig.bed.currentModel)
  const isLoadingBedVariation = useAppStore((s) => s.isLoadingBedVariation)
  
  // URL-driven render tuning for screenshots and debugging
  const urlParams = new URLSearchParams(window.location.search)
  // Quality: default low, force high via ?quality=high
  const qualityParam = urlParams.get('quality')
  const forceHighQuality = qualityParam === 'high'
  // Shadows: allow disabling via ?shadows=off|0|false
  const shadowsParam = urlParams.get('shadows')
  const disableShadows =
    shadowsParam === 'off' ||
    shadowsParam === '0' ||
    shadowsParam === 'false'
  // Camera preset for screenshots: ?camera=<key> (reuses tabCameraPositions keys)
  const cameraPresetKey = urlParams.get('camera') || urlParams.get('cam') || null
  // Optional filter to render only selected groups.
  // Accept both explicit query params and values embedded in ?config=<base64>.
  const showOnlyParam = useMemo(() => {
    const direct = urlParams.get('showOnly') || urlParams.get('show_only')
    if (direct) return direct

    const encodedConfig = urlParams.get('config')
    if (!encodedConfig) return null

    try {
      const decoded = JSON.parse(atob(encodedConfig))
      const configShowOnly = decoded?.showOnly ?? decoded?.show_only
      if (Array.isArray(configShowOnly)) return configShowOnly.join(',')
      if (typeof configShowOnly === 'string') return configShowOnly
    } catch {
      // Ignore malformed config payload and fall back to default behavior.
    }

    return null
  }, [urlParams])
  const visibleAccessoryParts = useMemo(() => {
    if (!showOnlyParam) return null
    const parts = showOnlyParam
      .split(',')
      .map((part) => part.trim().toLowerCase())
      .filter(Boolean)
    return new Set(parts)
  }, [showOnlyParam])
  const isAccessoryPartVisible = useCallback(
    (part: 'legs' | 'headboard' | 'topper' | 'bedsidetable' | 'ottoman' | 'accessories') => {
      if (!visibleAccessoryParts) return true
      return visibleAccessoryParts.has(part)
    },
    [visibleAccessoryParts]
  )
  const screenshotEntrypoint = isScreenshotEntrypoint()
  const transparentBackground = useMemo(() => {
    const isScreenshotMode =
      urlParams.get('screenshot') === '1' || urlParams.get('screenshot') === 'true'
    const noUI = urlParams.get('noUI') === 'true' || urlParams.get('noUI') === ''
    return screenshotEntrypoint || isScreenshotMode || noUI
  }, [urlParams, screenshotEntrypoint])
  const shouldHideBaseBedModel = useMemo(() => {
    if (!screenshotEntrypoint || !visibleAccessoryParts) return false
    return !visibleAccessoryParts.has('bed')
  }, [screenshotEntrypoint, visibleAccessoryParts])
  
  // Detect mobile device for automatic quality reduction
  const isMobile = useMemo(() => isMobileDevice(size.width), [size.width])
  
  // Set pixel ratio lower on mobile devices for better performance.
  // For the dedicated /screenshot entrypoint we clamp to 1 to avoid
  // effectively rendering at 8K when generating 4K frames.
  useEffect(() => {
    if (screenshotEntrypoint) {
      gl.setPixelRatio(1)
    } else if (isMobile) {
      const maxPixelRatio = 1.5
      gl.setPixelRatio(Math.min(window.devicePixelRatio, maxPixelRatio))
    } else {
      gl.setPixelRatio(window.devicePixelRatio)
    }
  }, [gl, isMobile])
  
  // Quality settings - lower by default for better performance, especially on mobile.
  // Mobile devices get even lower quality unless explicitly forced to high.
  const isHighQuality = forceHighQuality && !isMobile

  // When rendering via the /screenshot entrypoint, keep shadows at a
  // moderate resolution even on desktop to reduce GPU/SwiftShader load.
  const isScreenshotEntrypointForQuality = screenshotEntrypoint

  const shadowMapSize = isScreenshotEntrypointForQuality
    ? [4096, 4096]
    : isHighQuality
    ? [8192, 8192]
    : isMobile
    ? [1024, 1024]
    : [2048, 2048]
  const shadowBlurSamples = isHighQuality ? 8 : isMobile ? 2 : 4
  const shadowRadius = isHighQuality ? 15 : isMobile ? 4 : 8
  // Use a slightly less aggressive bias for screenshots to reduce light leaks / glowing edges.
  const shadowBias = isScreenshotEntrypointForQuality ? -0.00002 : -0.0001

  const aoQuality = isHighQuality && !isScreenshotEntrypointForQuality ? 'high' : 'performance'
  const aoSamples = isHighQuality && !isScreenshotEntrypointForQuality ? 16 : isMobile ? 4 : 8
  // AO tuning:
  // - For /screenshot we want tighter, stronger contact shadows without wide halos.
  // - For the interactive app we keep a slightly broader, softer AO.
  const aoRadius = isScreenshotEntrypointForQuality ? 0.25 : 0.5
  const aoIntensity = isScreenshotEntrypointForQuality ? 0.9 : 1
  const aoDistanceFalloff = isScreenshotEntrypointForQuality ? 2.0 : 1.0
  
  // Memoize post-processing effects to avoid re-renders
  const postProcessingEffects = useMemo(() => {
    const effects = [
      <N8AO
        key="n8ao"
        quality={aoQuality}
        aoRadius={aoRadius}
        intensity={aoIntensity}
        distanceFalloff={aoDistanceFalloff}
        color="#000000"
        // For screenshots we disable screenSpaceRadius so AO sticks
        // closer to actual geometry and reduces screen-edge artifacts.
        screenSpaceRadius={!isScreenshotEntrypointForQuality}
        aoSamples={aoSamples}
      />,
    ]
    if (isHighQuality) {
      effects.push(<SMAA key="smaa" />)
    }
    return effects
  }, [aoQuality, aoSamples, isHighQuality])
  
  // Memoize stitch color to avoid creating new references on every render
  // Only recalculate when the actual textile ID or stitch_color changes
  const stitchColor = useMemo((): string | undefined => {
    const color = bedConfig.textile?.stitch_color
    if (!color || color === null || color === '') return undefined
    return color
  }, [bedConfig.textile?.id, bedConfig.textile?.stitch_color])
  const legSelection: any = useAppStore((s) => s.bedConfig.legs)
  const headboard: any = useAppStore((s) => s.bedConfig.headboard)
  const currentView = useAppStore((s) => s.currentView)
  const SHOW_LIGHT_HELPERS = false // Toggle light helpers on/off

  // Get ottoman leg selection (must be called unconditionally at top level)
  const isSaturn = isSaturnOttoman(bedConfig.ottoman)
  const { ottomanLegSelection, ottomanLiftY: saturnLiftY } = useOttomanLegSelection(isSaturn)
  // Store previous camera state for restoration
  const previousCameraState = useRef<{
    position: THREE.Vector3
    target: THREE.Vector3
  } | null>(null)
  
  const [bedLiftY, setBedLiftY] = useState(0)
  const [bedBBoxReady, setBedBBoxReady] = useState(false)
  const [topperReadyByKey, setTopperReadyByKey] = useState<Record<string, boolean>>({})
  const [textileReady, setTextileReady] = useState(false)
  const [loadedLegsKey, setLoadedLegsKey] = useState<string | null>(null)
  // Force re-render when headboard changes to ensure it renders
  const [headboardRenderKey, setHeadboardRenderKey] = useState(0)
  
  // Track if camera animation is in progress to prevent restarting
  const isAnimatingRef = useRef(false)
  // Track the last model URL we calculated bounding box for - prevents recalculation
  const lastCalculatedModelUrlRef = useRef<string | null>(null)
  // Previous model URL for reset — do NOT read window.__bed_bbox_model_url__ here;
  // BedModel's child effect can overwrite that global before this parent's reset effect runs.
  const prevBedModelUrlRef = useRef<string | null>(null)
  // Track if we've initialized (to prevent clearing cache multiple times in StrictMode)
  const hasInitializedRef = useRef(false)

  // Always keep shadow map enabled; the ?shadows=off flag only disables
  // floor-receiving shadows, not all lighting/shading.
  useEffect(() => {
    gl.shadowMap.enabled = true
  }, [gl])
  
  // Clear any stale bounding box cache on FIRST mount only if it doesn't match current model
  // Empty dependency array ensures this only runs once on mount, not when model changes
  useEffect(() => {
    if (hasInitializedRef.current) return // Only run once, even in StrictMode
    hasInitializedRef.current = true
    
    const currentModelUrl = currentModel?.file_url
    const cachedModelUrl = (window as any).__bed_bbox_model_url__
    
    // Only clear if cached model URL doesn't match current model URL (stale cache from previous session)
    if (cachedModelUrl && currentModelUrl && cachedModelUrl !== currentModelUrl) {
      ;(window as any).__bed_bbox__ = null
      ;(window as any).__bed_bbox_model_url__ = null
      lastCalculatedModelUrlRef.current = null
      setBedBBoxReady(false)
    }
  }, []) // Empty array - only run on mount, not when model changes
  
  // Ensure bedBBoxReady is set to true if we have a cached bounding box
  useEffect(() => {
    const currentModelUrl = currentModel?.file_url
    const existingBBox = (window as any).__bed_bbox__
    const existingModelUrl = (window as any).__bed_bbox_model_url__
    
    // If we have a cached bounding box for the current model, ensure bedBBoxReady is true
    if (currentModelUrl && existingBBox && !existingBBox.isEmpty() && existingModelUrl === currentModelUrl && !bedBBoxReady) {
      setBedBBoxReady(true)
    }
  }, [currentModel?.file_url, bedBBoxReady])
  
  // Reset bed bounding box ONLY when model URL actually changes (not on every render).
  // useLayoutEffect so this clears before BedModel's useEffect calls onModelReady —
  // otherwise the child can write the new bbox, then we wipe it and never recalculate.
  useLayoutEffect(() => {
    const currentModelUrl = currentModel?.file_url
    const previousModelUrl = prevBedModelUrlRef.current

    // Only clear if we have a new model URL that's different from the previous one.
    // Compare against a ref (not window.__bed_bbox_model_url__) so a child BedModel
    // effect that already wrote the new URL can't make this reset a no-op.
    if (currentModelUrl && previousModelUrl && currentModelUrl !== previousModelUrl) {
      ;(window as any).__bed_bbox__ = null
      ;(window as any).__bed_bbox_model_url__ = null
      lastCalculatedModelUrlRef.current = null
      setBedBBoxReady(false)
    }
    if (currentModelUrl) {
      prevBedModelUrlRef.current = currentModelUrl
    }
  }, [currentModel?.file_url])

  const topperScope = JSON.stringify([
    currentModel?.file_url, bedConfig.topper?.id, bedConfig.topper?.three_d_model?.file_url,
    bedConfig.topper?.slug, bedConfig.textile?.currentTexture?.id,
  ])
  const topperReadyKey = (part: string) => `${topperScope}:${part}`
  const expectedTopperReadyKeys = useMemo(() => {
    const keys: string[] = []
    if (!isAccessoryPartVisible('topper') || !bedBBoxReady) return keys
    const topper = bedConfig.topper
    if (!topper?.three_d_model?.file_url) return keys

    const bedVariation = bedConfig.bed.variation
    const isAdjustable = bedVariation?.bed_type_value === 'adjustable'
    const selectedWidth = bedConfig.bed.selectedSize
    const threeDModels = topper.three_d_models || []

    if (isAdjustable && selectedWidth && selectedWidth >= 150 && threeDModels.length > 0) {
      const halfWidth = Math.floor(selectedWidth / 2)
      let leftModel = threeDModels.find((m: any) => m.width_cm === halfWidth && !m.is_split && m.reclined_side === 'left')
      let rightModel = threeDModels.find((m: any) => m.width_cm === halfWidth && !m.is_split && m.reclined_side === 'right')

      if ((!leftModel || !rightModel) && selectedWidth === 150) {
        if (!leftModel) {
          leftModel = threeDModels.find((m: any) => m.width_cm === selectedWidth && !m.is_split && (m.reclined_side === 'left' || String(m.reclined_side).toLowerCase() === 'left'))
        }
        if (!rightModel) {
          rightModel = threeDModels.find((m: any) => m.width_cm === selectedWidth && !m.is_split && (m.reclined_side === 'right' || String(m.reclined_side).toLowerCase() === 'right'))
        }
      }

      if (leftModel?.three_d_model?.file_url && rightModel?.three_d_model?.file_url) {
        keys.push(topperReadyKey(`topper-left-${topper.id}`), topperReadyKey(`topper-right-${topper.id}`))
        return keys
      }
    }

    keys.push(topperReadyKey(`topper-main-${topper.id}`))
    return keys
  }, [topperScope, isAccessoryPartVisible, bedBBoxReady, bedConfig.topper?.id, bedConfig.topper?.three_d_model?.file_url, bedConfig.topper?.three_d_models, bedConfig.bed.variation?.bed_type_value, bedConfig.bed.selectedSize])

  const handleTopperRenderReadyChange = useCallback((key: string, ready: boolean) => {
    setTopperReadyByKey((prev) => {
      if (prev[key] === ready) return prev
      return { ...prev, [key]: ready }
    })
  }, [])

  // Scope keys to the configuration instead of resetting in a parent effect.
  // A parent reset can erase a cached child's ready notification permanently.

  const textileExpected = !!(
    bedConfig.textile?.currentTexture ||
    (Array.isArray(bedConfig.textile?.textures) && bedConfig.textile.textures.length > 0)
  )

  // BedModel already loads textile maps via Suspense/useLoader; this just waits for apply.
  useEffect(() => {
    if (!currentModel) {
      setTextileReady(false)
      return
    }
    if (!textileExpected) {
      setTextileReady(true)
      return
    }
    setTextileReady(false)
    const onApplied = () => setTextileReady(true)
    window.addEventListener('bed-textile-applied', onApplied)
    if ((window as any).__bed_textile_base_map__) {
      setTextileReady(true)
    }
    return () => window.removeEventListener('bed-textile-applied', onApplied)
  }, [
    currentModel?.file_url,
    textileExpected,
    bedConfig.textile?.id,
    bedConfig.textile?.currentTexture?.id,
  ])

  const topperEnabled = !!(
    isAccessoryPartVisible('topper') &&
    bedConfig.topper?.three_d_model?.file_url
  )
  const topperWaitingForBedBBox = topperEnabled && !bedBBoxReady
  const topperReady = topperWaitingForBedBBox
    ? false
    : expectedTopperReadyKeys.length === 0
    ? true
    : expectedTopperReadyKeys.every((key) => topperReadyByKey[key] === true)

  // Bed legs: Suspense-load FBX after bbox; don't flip screenshot ready until loaded.
  const legsExpected =
    isAccessoryPartVisible('legs') &&
    !!legSelection
  const legsKey = JSON.stringify([
    currentModel?.file_url, legSelection?.id, legSelection?.three_d_model?.file_url,
    legSelection?.currentTexture?.id,
  ])
  const legsReady = !legsExpected || (
    !!legSelection?.three_d_model?.file_url && loadedLegsKey === legsKey
  )

  useEffect(() => {
    if (!screenshotEntrypoint) return
    setScreenshotRenderError('legs',
      !isLoadingBedVariation && legsExpected && !legSelection?.three_d_model?.file_url
        ? `Selected leg ${legSelection?.id} (${legSelection?.slug || 'unknown'}) has no 3D model for bed width ${bedConfig.bed.selectedSize}`
        : null)
  }, [screenshotEntrypoint, isLoadingBedVariation, legsExpected, legsKey, bedConfig.bed.selectedSize])

  const handleLegsRenderReadyChange = useCallback((ready: boolean) => {
    setLoadedLegsKey((previous) => ready ? legsKey : previous === legsKey ? null : previous)
  }, [legsKey])

  // Saturn fetches ottoman legs async — don't flip ready until that resolves
  // (so OttomanLegs can mount and Suspense-load before Playwright settles).
  const saturnLegsPending =
    isSaturn &&
    !!bedConfig.ottoman?.three_d_model?.file_url &&
    isAccessoryPartVisible('ottoman') &&
    ottomanLegSelection === null

  useEffect(() => {
    // Wait for bbox so legs/tables/ottoman have mounted (and can Suspense) before ready.
    // Headboards already use useLoader for textures; wood maps still finish in Playwright's settle.
    const ready =
      !isLoadingBedVariation &&
      !!currentModel &&
      bedBBoxReady &&
      textileReady &&
      topperReady &&
      legsReady &&
      !saturnLegsPending
    if (isScreenshotEntrypoint()) {
      ;(window as any).__screenshot_ready_gates__ = {
        hasModel: !!currentModel,
        isLoadingBedVariation,
        bedBBoxReady,
        textileReady,
        topperReady,
        legsReady,
        legsExpected,
        legId: legSelection?.id ?? null,
        legModelUrl: legSelection?.three_d_model?.file_url ?? null,
        saturnLegsPending,
        ready,
        hasBbox: !!(window as any).__bed_bbox__,
      }
      return signalScreenshotBedRenderReadyAfterApply(ready)
    } else {
      ;(window as any).__bed_render_ready__ = ready
    }
  }, [
    currentModel?.file_url,
    isLoadingBedVariation,
    bedBBoxReady,
    textileReady,
    topperReady,
    legsReady,
    legsExpected,
    legsKey,
    saturnLegsPending,
  ])
  
  // Memoized callback to prevent recalculation
  const handleBedModelReady = useCallback((bedFbx: THREE.Object3D) => {
    const currentModelUrl = currentModel?.file_url
    
    if (!currentModelUrl) return
    
    // DOUBLE CHECK: Use ref to ensure we never recalculate for the same URL
    if (lastCalculatedModelUrlRef.current === currentModelUrl) {
      const existingBBox = (window as any).__bed_bbox__
      if (existingBBox && !existingBBox.isEmpty()) {
        setBedBBoxReady(true)
        return
      }
    }
    
    const existingBBox = (window as any).__bed_bbox__
    const existingModelUrl = (window as any).__bed_bbox_model_url__
    
    // STRICT CHECK: If we already have a bounding box for this EXACT model URL, NEVER recalculate
    if (existingBBox && !existingBBox.isEmpty() && existingModelUrl === currentModelUrl) {
      lastCalculatedModelUrlRef.current = currentModelUrl
      setBedBBoxReady(true)
      return
    }
    
    // Only calculate if we don't have a bounding box OR the model URL changed
    // Calculate bounding box from bed model (needed for topper positioning)
    // IMPORTANT: Only calculate from bedFbx itself, NOT from any parent group
    // The topper is rendered inside the group, but we must NOT include it in the bounding box
    bedFbx.updateMatrixWorld(true)

    const bedBBox = new THREE.Box3().setFromObject(bedFbx)

    // setFromObject returns a world-space AABB. Bed + topper live under the
    // leg-lift group, and consumers treat __bed_bbox__ as that group's local
    // space. If legs already applied bedLiftY when this runs (e.g. seamless
    // model swap), convert out of the parent transform or topper Y double-counts lift.
    if (bedFbx.parent) {
      const parentInverse = new THREE.Matrix4().copy(bedFbx.parent.matrixWorld).invert()
      bedBBox.applyMatrix4(parentInverse)
    }
    
    // Only store if we got a valid bounding box
    if (!bedBBox.isEmpty()) {
      // Create a DEEP copy to prevent accidental modification
      // Box3.copy() only does a shallow copy - we need to copy the Vector3s too
      const frozenBBox = new THREE.Box3(
        bedBBox.min.clone(),
        bedBBox.max.clone()
      )
      
      ;(window as any).__bed_bbox__ = frozenBBox
      ;(window as any).__bed_bbox_model_url__ = currentModelUrl
      lastCalculatedModelUrlRef.current = currentModelUrl
      setBedBBoxReady(true)
    }
  }, [currentModel?.file_url])
  
  // Watch for headboard changes and force re-render
  useEffect(() => {
    if (headboard?.three_d_model?.file_url && currentModel) {
      setHeadboardRenderKey(prev => prev + 1)
    }
  }, [
    headboard?.id,
    headboard?.three_d_model?.id,
    headboard?.three_d_model?.file_url,
    currentModel?.id
  ])

  // Optional debug logging for headboard material flow.
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const debugHeadboard =
      urlParams.get('hbdebug') === '1' ||
      (window as any).__HB_DEBUG__ === true
    if (!debugHeadboard) return
    if (!headboard?.three_d_model?.file_url) return

    const headboardTextile = bedConfig.textile?.headboard_textile_for || null
    const sourceSlots = (window as any).__headboard_model_material_slots__ || []

    console.log('[HeadboardDebug] Scene selection state', {
      headboardId: headboard.id,
      headboardModelUrl: headboard.three_d_model.file_url,
      selectedTextileId: bedConfig.selectedTextileId,
      bedTextureId: bedConfig.textile?.currentTexture?.id || null,
      headboardTextileId: headboardTextile?.id || null,
      headboardTextureId: headboardTextile?.currentTexture?.id || null,
      headboardTextureSlotCount: Array.isArray(headboardTextile?.textures) ? headboardTextile.textures.length : 0,
      headboardTextureSlots: (headboardTextile?.textures || []).map((t: any) => ({
        slot_name: t.slot_name,
        slot_label: t.slot_label,
        texture_id: t.texture?.id || null,
      })),
      sourceSlotCount: sourceSlots.length,
      sourceSlots,
    })
  }, [
    headboard?.id,
    headboard?.three_d_model?.file_url,
    bedConfig.selectedTextileId,
    bedConfig.textile?.currentTexture?.id,
    bedConfig.textile?.headboard_textile_for?.id,
    bedConfig.textile?.headboard_textile_for?.currentTexture?.id,
    JSON.stringify((bedConfig.textile?.headboard_textile_for?.textures || []).map((t: any) => ({
      slot_name: t.slot_name,
      texture_id: t.texture?.id || null,
    }))),
  ])
  
  // Find tray and pocket accessories
  const tray = useMemo(() => {
    return bedConfig.accessories?.find((acc: any) => 
      acc.slug?.includes('tray') || acc.name?.toLowerCase().includes('tray')
  )
}, [bedConfig.accessories])

const pocket = useMemo(() => {
  return bedConfig.accessories?.find((acc: any) => 
    acc.slug?.includes('pocket') || acc.name?.toLowerCase().includes('pocket')
)
}, [bedConfig.accessories])

useEffect(() => {
  if (legSelection?.three_d_model?.file_url) {
    const loader = createNormalizedFBXLoader()
    loader.load(normalizeAssetUrl(legSelection.three_d_model.file_url), (leg) => {
      const legBox = new THREE.Box3().setFromObject(leg)
      const legHeight = (legBox.max.y - legBox.min.y) * 0.05
      const flyingOpticsLiftAdjustment = legSelection?.slug?.includes('flying-optics') ? -0.10 : 0
      const finalBedLiftY = legHeight + flyingOpticsLiftAdjustment
      setBedLiftY(finalBedLiftY)
      ;(window as any).__bed_lift_y__ = finalBedLiftY
    })
  } else {
    setBedLiftY(0)
    ;(window as any).__bed_lift_y__ = 0
  }
}, [legSelection?.three_d_model?.file_url, legSelection?.slug])

useEffect(() => {
  if (transparentBackground) {
    scene.background = null
    scene.fog = null
    gl.setClearColor(0x000000, 0)
  } else {
    scene.background = new THREE.Color('#fff')
    scene.fog = new THREE.Fog('#fff', 40, 60)
    gl.setClearColor(0xffffff, 1)
  }
}, [scene, gl, transparentBackground])

// Expose controls to window for UI access
  useEffect(() => {
    if (controlsRef.current) {
      ;(window as any).__orbit_controls__ = controlsRef.current
      // Expose camera and renderer for easy debugging and PNG capture
      ;(window as any).__camera__ = camera
      ;(window as any).__gl__ = gl
      ;(window as any).__scene__ = scene
      ;(window as any).__getCameraPosition__ = () => {
        const pos = camera.position
        const target = controlsRef.current?.target
        return {
          position: [pos.x, pos.y, pos.z],
          target: target ? [target.x, target.y, target.z] : null
        }
      }
    }
    return () => {
      ;(window as any).__orbit_controls__ = null
      ;(window as any).__camera__ = null
      ;(window as any).__gl__ = null
      ;(window as any).__scene__ = null
      ;(window as any).__getCameraPosition__ = null
    }
  }, [controlsRef.current, camera, gl, scene])

  // Track WebGL context loss/restoration for VPS debugging
  useEffect(() => {
    const canvas = gl.domElement
    const handleContextLost = (event: any) => {
      console.error('[WebGL] context lost', event?.statusMessage || '')
      ;(window as any).__webgl_context_lost__ = true
    }
    const handleContextRestored = () => {
      console.log('[WebGL] context restored')
      ;(window as any).__webgl_context_lost__ = false
    }
    canvas.addEventListener('webglcontextlost', handleContextLost)
    canvas.addEventListener('webglcontextrestored', handleContextRestored)
    return () => {
      canvas.removeEventListener('webglcontextlost', handleContextLost)
      canvas.removeEventListener('webglcontextrestored', handleContextRestored)
    }
  }, [gl])

// Camera animation for tab changes
useEffect(() => {
  // Disable tab-based camera animations on the dedicated /screenshot entrypoint;
  // screenshots use the URL-driven camera presets instead.
  if (screenshotEntrypoint) return
  if (!controlsRef.current || !bedBBoxReady || isAnimatingRef.current) return
  
  const controls = controlsRef.current
  
  if (currentView && Object.keys(tabCameraPositions).includes(currentView)) {
  // Store current camera state if not already stored
  if (!previousCameraState.current) {
    previousCameraState.current = {
      position: camera.position.clone(),
      target: controls.target.clone()
    }
  }
  
  // Detect mobile and get appropriate camera position
  const tabCamera = getTabCameraPosition(currentView, isMobile)
  if (!tabCamera) return
  
  const targetPos = new THREE.Vector3(...tabCamera.target)
  const cameraPos = new THREE.Vector3(...tabCamera.position)
  
  // Temporarily disable damping for smooth animation
  const wasDampingEnabled = controls.enableDamping
  controls.enableDamping = false
  
  // Mark animation as in progress
  isAnimatingRef.current = true
  
  // Animate camera to tab position
  // Use controls.object.position instead of camera.position for OrbitControls compatibility
  const startPos = controls.object.position.clone()
  const startTarget = controls.target.clone()
  const startTime = performance.now()
  const duration = 800 // milliseconds
  
  const animate = () => {
    const elapsed = performance.now() - startTime
    const progress = Math.min(elapsed / duration, 1)
    
    // Easing function for smooth animation
    const easeOut = 1 - Math.pow(1 - progress, 3)
    
    // Interpolate position and target using controls.object for OrbitControls compatibility
    controls.object.position.lerpVectors(startPos, cameraPos, easeOut)
    controls.target.lerpVectors(startTarget, targetPos, easeOut)
    controls.update()
    
    if (progress < 1) {
      requestAnimationFrame(animate)
    } else {
      // Re-enable damping and mark animation as complete
      controls.enableDamping = wasDampingEnabled
      isAnimatingRef.current = false
    }
  }
  
  animate()
} else {
  // Restore previous camera state when leaving tab
  if (previousCameraState.current && !isAnimatingRef.current) {
    // Temporarily disable damping for smooth animation
    const wasDampingEnabled = controls.enableDamping
    controls.enableDamping = false
    
    // Mark animation as in progress
    isAnimatingRef.current = true
    
    // Use controls.object.position instead of camera.position for OrbitControls compatibility
    const startPos = controls.object.position.clone()
    const startTarget = controls.target.clone()
    const savedState = previousCameraState.current
    const startTime = performance.now()
    const duration = 800
    
    const animate = () => {
      const elapsed = performance.now() - startTime
      const progress = Math.min(elapsed / duration, 1)
      
      const easeOut = 1 - Math.pow(1 - progress, 3)
      
      controls.object.position.lerpVectors(startPos, savedState.position, easeOut)
      controls.target.lerpVectors(startTarget, savedState.target, easeOut)
      controls.update()
      
      if (progress < 1) {
        requestAnimationFrame(animate)
      } else {
        // Re-enable damping, clear stored state, and mark animation as complete
        controls.enableDamping = wasDampingEnabled
        previousCameraState.current = null
        isAnimatingRef.current = false
      }
    }
    
    animate()
  }
}
}, [currentView, bedBBoxReady, camera, size, isScreenshotEntrypoint])

// Apply camera preset for /screenshot via ?camera=<key> or ?cam=<key>.
useEffect(() => {
  if (!isScreenshotEntrypoint || !cameraPresetKey || !controlsRef.current || !bedBBoxReady) return
  const controls = controlsRef.current
  const preset = getTabCameraPosition(cameraPresetKey, isMobile)
  if (!preset) return

  const targetPos = new THREE.Vector3(...preset.target)
  const cameraPos = new THREE.Vector3(...preset.position)

  // Directly set camera + controls target, then update once.
  controls.object.position.copy(cameraPos)
  controls.target.copy(targetPos)
  controls.update()
  // eslint-disable-next-line no-console
  console.log('[Scene] applied screenshot camera preset', { cameraPresetKey, position: preset.position, target: preset.target })
}, [isScreenshotEntrypoint, cameraPresetKey, bedBBoxReady, isMobile])

const dirLight1Ref = useRef<THREE.DirectionalLight>(null)

const rectAreaLightRef = useRef<THREE.RectAreaLight>(null)
const rectAreaLight2Ref = useRef<THREE.RectAreaLight>(null)
const dirLight1HelperRef = useRef<DirectionalLightHelper | null>(null)
const rectAreaLightHelperRef2 = useRef<RectAreaLightHelper | null>(null)
const rectAreaLightHelperRef = useRef<RectAreaLightHelper | null>(null)

// Create and update light helpers
useEffect(() => {
  if (!SHOW_LIGHT_HELPERS) {
    // Clean up helpers if disabled
    if (dirLight1HelperRef.current) {
      scene.remove(dirLight1HelperRef.current)
      dirLight1HelperRef.current.dispose()
      dirLight1HelperRef.current = null
    }
    if (rectAreaLightHelperRef2.current) {
      scene.remove(rectAreaLightHelperRef2.current)
      rectAreaLightHelperRef2.current.dispose()
      rectAreaLightHelperRef2.current = null
    }
    if (rectAreaLightHelperRef.current) {
      scene.remove(rectAreaLightHelperRef.current)
      rectAreaLightHelperRef.current.dispose()
      rectAreaLightHelperRef.current = null
    }
    return
  }
  
  if (dirLight1Ref.current && !dirLight1HelperRef.current) {
    const helper = new THREE.DirectionalLightHelper(dirLight1Ref.current, 5, '#ff6b6b')
    dirLight1HelperRef.current = helper
    scene.add(helper)
  } 
  if (rectAreaLight2Ref.current && !rectAreaLightHelperRef2.current) {
    const helper = new RectAreaLightHelper(rectAreaLight2Ref.current, '#4ecdc4')
    rectAreaLightHelperRef2.current = helper
    scene.add(helper)
  }
  if (rectAreaLightRef.current && !rectAreaLightHelperRef.current) {
    const helper = new RectAreaLightHelper(rectAreaLightRef.current, '#95e1d3')
    rectAreaLightHelperRef.current = helper
    scene.add(helper)
  }
  
  return () => {
    if (dirLight1HelperRef.current) {
      scene.remove(dirLight1HelperRef.current)
      dirLight1HelperRef.current.dispose()
      dirLight1HelperRef.current = null
    }
    if (rectAreaLightHelperRef2.current) {
      scene.remove(rectAreaLightHelperRef2.current)
      rectAreaLightHelperRef2.current.dispose()
      rectAreaLightHelperRef2.current = null
    }
    if (rectAreaLightHelperRef.current) {
      scene.remove(rectAreaLightHelperRef.current)
      rectAreaLightHelperRef.current.dispose()
      rectAreaLightHelperRef.current = null
    }
  }
}, [scene, SHOW_LIGHT_HELPERS])

// Update helpers when lights move
useEffect(() => {
  if (!SHOW_LIGHT_HELPERS) return
  
  if (dirLight1HelperRef.current) {
    dirLight1HelperRef.current.update()
  }
  if (rectAreaLightHelperRef2.current) {  
    rectAreaLightHelperRef2.current.dispose()
  }
  // RectAreaLightHelper updates automatically, no need to call update()
})


return (
  <>
  <CameraAdjuster />
  <directionalLight
  ref={dirLight1Ref}
  position={[16, 10, 21]}
  lookAt={[0, 2, 0]}
  intensity={1.5}
  color="#fff"
  castShadow
  shadow-mapSize={shadowMapSize}
  shadow-camera-left={-30}
  shadow-camera-right={30}
  shadow-camera-top={30}
  shadow-camera-bottom={-30}
  shadow-camera-near={0.1}
  shadow-camera-far={80}
  shadow-bias={shadowBias}
  shadow-radius={shadowRadius}
  shadow-blurSamples={shadowBlurSamples}
  />
   <rectAreaLight
  ref={rectAreaLightRef}
  position={[0, 9, 0]}
  intensity={.7}
  color="#fff"
  width={20}
  height={20}
  lookAt={[0, 0, 0]}
  rotation={[-(Math.PI / 2), 0, 0]}
  />
  <rectAreaLight
  ref={rectAreaLight2Ref}
  position={[0, 5, 10]}
  intensity={1.75}
  color="#fff"
  width={14}
  height={5}
  lookAt={[0, 0, 0]}
  rotation={[0, 0, 0]}
  />
  
  <Environment preset="apartment" environmentIntensity={.2} environmentRotation={[0,0, 0]}/>
  {/* <Environment files={hdr} environmentIntensity={.2} environmentRotation={[0, Math.PI / 8, 0]}  /> */}
  {/* Shadow receiving floor (transparent). Radial fade hides the plane edge on export;
      fog + fade in viewer mode. ?shadows=off disables floor shadows only. */}
  <ShadowFloor
    disabled={disableShadows}
    variant={transparentBackground ? 'export' : 'viewer'}
  />
  {/* Extra contact shadows under the bed/legs for crisper grounding.
      Enabled for all modes; most important for the /screenshot entrypoint. */}

  
  {currentModel ? (
    <group position={[0, bedLiftY, 0]} visible={!shouldHideBaseBedModel}>
    <BedModel 
    modelUrl={normalizeAssetUrl(currentModel.file_url)}
    onModelReady={handleBedModelReady}
    />
{isAccessoryPartVisible('topper') && bedBBoxReady && (() => {
      const topper = bedConfig.topper
      if (!topper?.three_d_model?.file_url) return null
      
      const bedVariation = bedConfig.bed.variation
      const isAdjustable = bedVariation?.bed_type_value === 'adjustable'
      const selectedWidth = bedConfig.bed.selectedSize
      const threeDModels = topper.three_d_models || []
      
      // Check if we need to load two reclined models (adjustable bed + non-split topper)
      // Only for beds >= 150cm - smaller beds (<150cm) use reclined models but render as single topper
      if (isAdjustable && selectedWidth && selectedWidth >= 150 && threeDModels.length > 0) {
        // Calculate half width (e.g., 180cm bed -> 90cm halves)
        const halfWidth = Math.floor(selectedWidth / 2)
        
        // Debug logging removed to reduce console spam
        
        // Find left and right reclined models at half width (for larger beds like 180cm)
        // AltReclined = right, reclined = left (swapped from original)
        let leftModel = threeDModels.find((m: any) => 
          m.width_cm === halfWidth && 
        !m.is_split && 
        m.reclined_side === 'left'
      )
      let rightModel = threeDModels.find((m: any) => 
        m.width_cm === halfWidth && 
      !m.is_split && 
      m.reclined_side === 'right'
    )
    
    // For 150cm beds, also check full width (since reclined models might be at full width for 150cm)
    if ((!leftModel || !rightModel) && selectedWidth === 150) {
      // Debug logging removed to reduce console spam
      
      // Look for reclined models at full width for 150cm beds
      if (!leftModel) {
        leftModel = threeDModels.find((m: any) => 
          m.width_cm === selectedWidth && 
          !m.is_split && 
          (m.reclined_side === 'left' || String(m.reclined_side).toLowerCase() === 'left')
        )
      }
      if (!rightModel) {
        rightModel = threeDModels.find((m: any) => 
          m.width_cm === selectedWidth && 
          !m.is_split && 
          (m.reclined_side === 'right' || String(m.reclined_side).toLowerCase() === 'right')
        )
      }
    }
    
    // Debug logging removed to reduce console spam
    
    // If we have both reclined models, render two halves side by side
    // This only applies to beds >= 150cm (beds < 150cm render single topper below)
    if (leftModel?.three_d_model?.file_url && rightModel?.three_d_model?.file_url) {
      // Calculate positioning: start with right topper at quarter-width position
      const BASE_SCALE = 0.05 // Same as Topper component
      const bedBox = (window as any).__bed_bbox__
      const bedWidthCm = bedBox ? (bedBox.max.x - bedBox.min.x) / BASE_SCALE : selectedWidth
      const quarterBedWidth = (bedWidthCm / 4) * BASE_SCALE

      // Right topper position - start at quarter width (to be tuned)
      const rightOffset = quarterBedWidth-.03
      
      // Left topper position - will be adjusted relative to right (to be tuned)
      const leftOffset = -quarterBedWidth+.04
      
      // Debug logging removed to reduce console spam
      
      return (
        <>
        <Topper
        key={`topper-left-${topper.id}`}
        topperUrl={normalizeAssetUrl(leftModel.three_d_model.file_url)}
        textureOptions={(topper as any).texture_options || []}
        positionOffset={[leftOffset, -.02, .05]}
        topperSlug={topper.slug}
        topperSlugEn={(topper as any).slug_en}
        renderReadyKey={topperReadyKey(`topper-left-${topper.id}`)}
        onRenderReadyChange={handleTopperRenderReadyChange}
        />
        <Topper
        key={`topper-right-${topper.id}`}
        topperUrl={normalizeAssetUrl(rightModel.three_d_model.file_url)}
        textureOptions={(topper as any).texture_options || []}
        positionOffset={[rightOffset, 0, 0]}
        topperSlug={topper.slug}
        topperSlugEn={(topper as any).slug_en}
        offsets={(topper as any).offsets || {}}
        renderReadyKey={topperReadyKey(`topper-right-${topper.id}`)}
        onRenderReadyChange={handleTopperRenderReadyChange}
        />
        </>
      )
    }
  }
  

  return (
    <Topper
    key={`topper-${topper.id}-${topper.three_d_model?.file_url || 'no-url'}`}
    topperUrl={normalizeAssetUrl(topper.three_d_model.file_url)}
    textureOptions={(topper as any).texture_options || []}
    topperSlug={topper.slug}
    topperSlugEn={(topper as any).slug_en}
    offsets={(topper as any).offsets || {}}
    renderReadyKey={topperReadyKey(`topper-main-${topper.id}`)}
    onRenderReadyChange={handleTopperRenderReadyChange}
    />
  )
})()}
{isAccessoryPartVisible('accessories') && bedBBoxReady && tray?.three_d_model?.file_url && (
  <Tray
  key={`tray-${tray.id}-${currentModel?.id || 'no-model'}`}
  trayUrl={normalizeAssetUrl(tray.three_d_model.file_url)}
  texture={tray.currentTexture || null}
  useUvChecker={false}
  rotateQuarterTurns={1}
  swapUV={true}
  />
)}
{isAccessoryPartVisible('accessories') && bedBBoxReady && pocket?.three_d_model?.file_url && (
  <Pocket
  key={`pocket-${pocket.id}-${currentModel?.id || 'no-model'}`}
  pocketUrl={normalizeAssetUrl(pocket.three_d_model.file_url)}
  useUvChecker={false}
  rotateQuarterTurns={1}
  swapUV={true}
  />
)}

</group>
) : (
  <mesh position={[0, 2, 0]}>
  <boxGeometry args={[8, 0.2, 2]} />
  <meshStandardMaterial color="#e5e7eb" />
  </mesh>
)}

{isAccessoryPartVisible('legs') && currentModel && bedBBoxReady && legSelection && (
  <Legs 
  key={`legs-${legSelection.id}-${legSelection.currentTexture?.id || 'no-texture'}`}
  bedUrl={normalizeAssetUrl(currentModel.file_url)}
  legUrl={normalizeAssetUrl(legSelection.three_d_model?.file_url || '')}
  texture={legSelection.currentTexture || null}
  useUvChecker={false}
  rotateQuarterTurns={1}
  swapUV={true}
  legTypeId={legSelection.legTypeId}
  legSlug={legSelection.slug}
  bedVariationId={bedConfig.bed.variation?.id}
  isSingleBed={typeof bedConfig.bed.selectedSize === 'number' && bedConfig.bed.selectedSize < 150}
  edgeInset={legSelection.edge_inset}
  onRenderReadyChange={handleLegsRenderReadyChange}
  />
)}

{isAccessoryPartVisible('bedsidetable') && currentModel && bedConfig.bedsideTable?.three_d_model?.file_url && (
  <BedsideTable
  tableUrl={normalizeAssetUrl(bedConfig.bedsideTable.three_d_model.file_url)}
  texture={bedConfig.bedsideTable.currentTexture || null}
  useUvChecker={false}
  rotateQuarterTurns={1}
  swapUV={true}
  bedLiftY={bedLiftY}
  />
)}

{isAccessoryPartVisible('ottoman') && currentModel && bedConfig.ottoman?.three_d_model?.file_url && (() => {
  // Saturn uses bed textile, others use their own texture
  const ottomanTexture = isSaturn 
    ? bedConfig.textile?.currentTexture || null
    : bedConfig.ottoman?.currentTexture || null
  
  return (
    <>
      <Ottoman
        ottomanUrl={normalizeAssetUrl(bedConfig.ottoman.three_d_model.file_url)}
        texture={ottomanTexture}
        useUvChecker={false}
        rotateQuarterTurns={1}
        swapUV={true}
        bedLiftY={bedLiftY}
        ottomanLiftY={isSaturn ? saturnLiftY : undefined}
      />
      {/* Render legs for Saturn ottoman */}
      {isSaturn && bedBBoxReady && ottomanLegSelection && ottomanLegSelection.three_d_model?.file_url && (
        <OttomanLegs
          ottomanUrl={normalizeAssetUrl(bedConfig.ottoman.three_d_model.file_url)}
          legUrl={normalizeAssetUrl(ottomanLegSelection.three_d_model.file_url)}
          texture={ottomanLegSelection.currentTexture || null}
          legTypeId={(ottomanLegSelection as any).legTypeId}
          edgeInset={legSelection?.edge_inset ?? (ottomanLegSelection as any).edge_inset}
        />
      )}
    </>
  )
})()}

{isAccessoryPartVisible('headboard') && currentModel && headboard?.three_d_model?.file_url && (
  <Headboard 
  key={`headboard-${headboard.id}-${headboard.three_d_model?.file_url || 'no-url'}-${bedConfig.selectedTextileId || 'no-textile'}-${currentModel?.id || 'no-model'}-${headboardRenderKey}`}
  headboardUrl={normalizeAssetUrl(headboard.three_d_model.file_url)}
  texture={bedConfig.textile?.currentTexture || null}
  headboardTexture={bedConfig.textile?.headboard_textile_for?.currentTexture || null}
  headboardTextureOptions={headboard?.texture_options || []}
  onFloor={headboard?.on_floor ?? false}
  offsets={headboard?.offsets || {}}
  />
)}

{isAccessoryPartVisible('accessories') && currentModel && bedBBoxReady && bedConfig.accessories?.length > 0 && (
  <Accessories
  key={`accessories-${bedConfig.accessories.map((acc: any) => acc.id).join('-')}`}
  useUvChecker={false}
  rotateQuarterTurns={1}
  swapUV={true}
  bedLiftY={bedLiftY}
  />
)}

<OrbitControls
ref={controlsRef}
makeDefault
minPolarAngle={0.1}
maxPolarAngle={Math.PI / 2 - 0.1}
minAzimuthAngle={-Math.PI / 2 + 0.1}
maxAzimuthAngle={Math.PI / 2 - 0.1}
enableZoom={true}
enablePan={true}
enableDamping={true}
dampingFactor={0.1}
rotateSpeed={0.8}
zoomSpeed={1.2}
panSpeed={0.8}
minDistance={10}
maxDistance={45}
target={[0, 2, 0]}
/>

<EffectComposer>
  {postProcessingEffects}
</EffectComposer>
</>
)
}
